import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Container,
    Row,
    Col,
    UncontrolledTooltip,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRotateRight,
    faCheck,
    faCircleExclamation,
    faCircleStop,
    faExclamationTriangle,
    faBugSlash,
    faGhost,
    faWrench,
    faArrowsRotate,
    faBan,
    faMagnifyingGlass,
    faFileLines,
} from "@fortawesome/free-solid-svg-icons";

import ReactTable from "components/ReactTable/ReactTable";
import useAgentStatus from "Hooks/useAgentStatus";
import useHelpers from "Hooks/useHelpers";
import { direction } from "Pages/Integration/Direction";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Run/RunDetails/RunDetails.module.css";
import useAlertContext from "Hooks/useAlertContext";
import IntegrationRunTime from "../Components/IntegrationRunTime";
import usePageTitle from "Hooks/usePageTitle";
import ControlledColumnFilter from "components/ReactTable/Components/ControlledColumnFilter";
import { ColumnInstance } from "react-table";
import MessageTimeColumnFilter from "./MessageTimeColumnFilter/MessageTimeColumnFilter";

interface IData {
    // null whenever the run has no matching `integration` row — the endpoint looks
    // one up by company + process + arg count (MessageHeaders.GetMessageDetailsAsync),
    // and DEBUG/preview runs or de-registered processes simply don't match one.
    integration: {
        direction: string;
        name: string;
        initiator: string;
        documentation_url?: string;
    } | null;
    message_details: {
        message_text: string;
        message_time: string;
        message_type: string;
    }[];
    page_count: number;
    total_records: number;
    message_header: {
        id: string;
        finish_time: string;
        initiator: string;
        machine_name: string;
        // null on runs written before the Runner stamped its version (dimension-4qgyb)
        runner_version: string | null;
        method_info: string;
        name: string;
        number_created: number;
        number_deleted: number;
        number_errored: number;
        number_updated: number;
        number_warning: number;
        run_time: number;
        start_time: string;
        status: string;
    };
}

export interface IFilters {
    created: boolean;
    deleted: boolean;
    errored: boolean;
    info: boolean;
    updated: boolean;
    warning: boolean;
    description: string;
    ascending: boolean;
}

const RUNNING_STATUSES = new Set(["Started", "Debugging"]);

const defaultFilters: IFilters = {
    created: true,
    deleted: true,
    updated: true,
    errored: true,
    warning: true,
    info: true,
    description: "",
    ascending: true,
};

/** Memoized so the expensive link/`[[DETAILS]]` parsing in formatMessageText only
 *  re-runs when a row's text or the show-details toggle actually changes — not on
 *  every 3s poll re-render of an already-rendered row. */
const MessageTextCell = memo(function MessageTextCell({
    value,
    showAllDetails,
}: {
    value: string;
    showAllDetails: boolean;
}) {
    const { formatMessageText } = useHelpers();
    return <span data-testid="messageText">{formatMessageText(value, showAllDetails)}</span>;
});

export default function RunDetails() {
    const [data, setData] = useState<IData>();
    const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);
    const [showAllDetails, setShowAllDetails] = useState(false);
    const [filterFocused, setFilterFocused] = useState(false);
    /** True value means the type of message is being displayed. False means it is hidden. */
    
    /** Added a pending filters state to track filter. Applied filters will 
     * only be used when the search button is clicked
     */
    const [pendingFilters, setPendingFilters] =
        useState<IFilters>(defaultFilters);
    const [appliedFilters, setAppliedFilters] =
        useState<IFilters>(defaultFilters);

    // Always-current snapshot of pendingFilters for the column filter renderers.
    // Reading these through a ref lets the `columns` useMemo omit pendingFilters
    // from its deps, so typing doesn't rebuild the columns array (which remounts
    // the filter <input> and steals focus on every keystroke).
    const pendingFiltersRef = useRef(pendingFilters);
    pendingFiltersRef.current = pendingFilters;

    
    const [showingAllMessageTypes, setShowingAllMessageTypes] = useState(true);
    const [triggerGoToPageOne, setTriggerGoToPageOne] = useState(false);

    usePageTitle("Run Details");
    const { newAlert } = useAlertContext();

    const { messageHeaderId } = useParams();
    const navigate = useNavigate();

    const requestInFlight = useRef(false);

    const {
        abandonJob,
        canAbandonJobs,
        canRerunAll,
        isAdmin,
        formatMessageText,
        formatSeconds,
        replacePlussesWithSpaces,
    } = useHelpers();

    const { get, post } = useFetch();
    useAgentStatus();

    /** Used inside useEffect to fetch every 1s if status is started or debugging */
    const fetchInterval = useMemo(
        () =>
            // Pause live polling while the user is in the search box, so the 3s
            // re-render doesn't steal focus / drop keystrokes mid-filter.
            filterFocused
                ? null
                : RUNNING_STATUSES.has(data?.message_header.status ?? "")
                  ? 3000
                  : null,
        [data?.message_header.status, filterFocused]
    );

    /** Copies pending filters to applied filters, which is a dependency of getDetails, 
     * which is a dependency of serverFetch, which is passed to the child table, which 
     * has an effect that fetches when this dependency chain changes. Fetching must be 
     * in the table component to fetch when the table library internal page state changes, 
     * otherwise this would probably be greatly simplified.
     */
    const handleSearch = useCallback(() => {
        setAppliedFilters(pendingFilters);
    }, [pendingFilters]);

    // Add event listener to call handleSearch when Enter is pressed
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                event.preventDefault();
                handleSearch();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleSearch]);

    const getDetails = useCallback(async ({ pageIndex = 0, pageSize = 25 } = {}) => {
        if (requestInFlight.current) {
            return;
        }
        requestInFlight.current = true;

        setIsLoading((prev) => (prev ?? true));

        try {
            let url = `/api/run/${messageHeaderId}?page_index=${pageIndex}&page_size=${pageSize}`;

            const response = await post(url, appliedFilters);
            const data = await response.json();
            console.log("RunDetails get messages response: ", data);

            if (response.ok) {
                setData(data);
                if (messageHeaderId === "latest") {
                    navigate(`/run_details/${data.message_header.id}`);
                }
            } else {
                newAlert({
                    message: data.detail ?? "Error loading message details",
                });
            }
        } catch (error) {
            console.error("RunDetails fetch error: ", error);
            newAlert({
                message: "Error loading message details",
            });
        } finally {
            requestInFlight.current = false;
            setIsLoading(false);
        }
    }, [messageHeaderId, post, appliedFilters, navigate, newAlert]);

    /** Triggers table to go to page 1 when filters change.*/
    useEffect(() => {
        setTriggerGoToPageOne((prev) => !prev);
    }, [appliedFilters]);

    // Keyboard scroll: Home / Ctrl+Cmd+ArrowUp -> top,
    // End / Ctrl+Cmd+ArrowDown -> bottom. The modifier+arrow pair covers
    // keyboards (laptops, Mac, compact) that lack dedicated Home/End keys.
    // Ignored while typing in an input.
    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            const mod = e.ctrlKey || e.metaKey;
            const toTop = e.key === "Home" || (mod && e.key === "ArrowUp");
            const toBottom = e.key === "End" || (mod && e.key === "ArrowDown");
            if (!toTop && !toBottom) return;
            // The search box autofocuses, so don't guard on input focus — these
            // keys should always jump the page. behavior:"instant" overrides
            // Bootstrap's :root { scroll-behavior: smooth }.
            e.preventDefault();
            window.scrollTo({
                top: toTop ? 0 : document.body.scrollHeight,
                behavior: "instant" as ScrollBehavior,
            });
        }
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    // Add event listeners to the links in the details within the collapsible pre tag,
    //  for the click here links that add a request to the job
    // queue and are not actual links. Not recommended approach, but necessary
    // because we are parsing and editing a string with formatMessageText in the useHelpers hook
    // to use inside the pre with dangerouslySetInnerHTML.
    useEffect(() => {
        if (!data) {
            return;
        }

        const table = document.querySelector("#reactTable");

        table?.addEventListener("click", sendToJobQueue);

        async function sendToJobQueue(e: any) {
            if (e.target.matches("a[id='addToJobQueueLink']")) {
                e.preventDefault();
                const target = e.target as HTMLElement;
                const url = target.dataset.url;

                if (!url) {
                    newAlert({
                        message: "No url on link for job queue request.",
                    });
                    return;
                }

                const response = await get(url);
                const data = await response.json();
                console.log(data);

                if (response.ok) {
                    newAlert({
                        message: "Request submitted successfully",
                        color: "success",
                    });
                } else {
                    newAlert({ message: "Request failed" });
                }
            }
        }

        return () => {
            table?.removeEventListener("click", sendToJobQueue);
        };
    }, [data, get, newAlert]);

    /** Button click handler to change data filters, the value is true if data is shown */
    const changeFilters = (filter: keyof IFilters): void => {
        const oppositeValue = !pendingFilters[filter];

        setPendingFilters((prev) => ({ ...prev, [filter]: oppositeValue }));
    };

    /** Passed down to the description column filter to set the description field of the 
     * filter state object.
     */
    const setDescriptionFilter = (value: string) => {
        setPendingFilters((prev) => ({ ...prev, description: value }));
    };

    /** Runs the integration again on button click */
    async function runIntegration(
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): Promise<void> {
        if (data === undefined) return;

        const method_info = replacePlussesWithSpaces(
            data.message_header.method_info
        );

        let url = `/api/jobqueue/${method_info}`;

        while (url.endsWith("/DEBUG")) {
            url = url.split("/").slice(0, -1).join("/");
        }
        if (e.currentTarget.name === "DEBUG") {
            url += "/DEBUG";
        }

        const response = await post(url);

        const json = await response.json();
        console.log(json);

        if (response.ok) {
            navigate("/run");
        } else {
            newAlert({
                color: "danger",
                message: `Error in run integration response ${
                    json.detail && ": " + json.detail
                }`,
            });
        }
    }

    async function getLogFile() {
        const response = await get(`/api/run/log/${messageHeaderId}`);

        if (response.ok) {
            const data = await response.blob();
            const blob = new Blob([data], {
                type: "text/plain;charset=utf-8",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Get filename from Content-Disposition header
            let fileName = getFileNameFromHeaders(response);

            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            newAlert({
                color: "danger",
                message: "No log file is available",
            });
        }
    }

    function getFileNameFromHeaders(response: Response) {
        let fileName = "run.log";
        if (response.headers.has("Content-Disposition")) {
            const contentDisposition =
                response.headers.get("Content-Disposition") ?? "";
            const parts = contentDisposition.split(";");
            for (const part of parts) {
                if (part.includes("filename=")) {
                    fileName = part.split("=")[1].trim();
                }
            }
        }
        return fileName;
    }

    /** Columns for ReactTable */
    const columns = useMemo(() => {
        return [
            {
                Header: "Time",
                accessor: "message_time",
                Filter: () => (
                    <MessageTimeColumnFilter filters={pendingFiltersRef.current} setFilters={setPendingFilters} setAppliedFilters={setAppliedFilters} />
                ),
                width: 125,
                disableSortBy: true,
                Cell: ({ row, value }: { row: any; value: string }) => {
                    const elapsed = getElapsedMessageDetailTime(value);
                    return (
                        <>
                            <span id={`messageTime${row.index}`}>
                                {format(new Date(value), "MMM d, HH:mm:ss ")}
                            </span>
                            <UncontrolledTooltip
                                target={`messageTime${row.index}`}
                            >
                                {`${format(
                                    new Date(value),
                                    "MMM d, y"
                                )} - ${elapsed} elapsed`}
                            </UncontrolledTooltip>
                        </>
                    );
                },
            },
            {
                Header: "Type",
                accessor: "message_type",
                width: 100,
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ value }: { value: string }) => {
                    // Map numeric MessageType enum values that message_service briefly
                    // wrote to the DB (commit 3f56b71d on 2026-05-05 changed message_type
                    // to a C# enum without a PetaPoco string converter on the
                    // message_service copy of the class; deployed 2026-05-18).
                    const numericMap: Record<string, string> = {
                        "0": "Info",
                        "1": "Warning",
                        "2": "Created",
                        "3": "Updated",
                        "4": "Errored",
                        "5": "Deleted",
                    };
                    const display = numericMap[value] ?? value;
                    switch ((display ?? "").toLowerCase()) {
                        case "created":
                            return (
                                <div className="text-info">
                                    <b>{display}</b>
                                </div>
                            );
                        case "deleted":
                            return (
                                <div className="text-black">
                                    <b>{display}</b>
                                </div>
                            );
                        case "updated":
                            return (
                                <div className="text-success">
                                    <b>{display}</b>
                                </div>
                            );
                        case "errored":
                        case "error":
                            return (
                                <div className="text-danger">
                                    <b>{display}</b>
                                </div>
                            );
                        case "warning":
                        case "warn":
                            return (
                                <div className="text-warning">
                                    <b>{display}</b>
                                </div>
                            );
                        case "info":
                        case "":
                            return <div>{display || "Info"}</div>;
                        default:
                            return <div>{display}</div>;
                    }
                },
            },
            {
                Header: "Description",
                accessor: "message_text",
                width: 800,
                disableSortBy: true,
                Filter: ({ column }: { column: ColumnInstance }) => (
                    <ControlledColumnFilter filterValue={pendingFiltersRef.current.description} setFilter={setDescriptionFilter} placeholder="Search… (space = AND, !term = exclude)" onFocusChange={setFilterFocused} />
                ),
                Cell: ({ value }: { value: string }) => (
                    <MessageTextCell value={value} showAllDetails={showAllDetails} />
                ),
            },
        ];

        /** Calculates the elapsed time for a message detail */
        function getElapsedMessageDetailTime(messageTime: string) {
            const startTimeDate = new Date(
                data?.message_header.start_time ?? ""
            );
            const messageTimeDate = new Date(messageTime);
            const elapsedInMs =
                messageTimeDate.getTime() - startTimeDate.getTime();

            const elapsedInSeconds = Math.floor(elapsedInMs / 1000);
            const hoursMinutesSeconds = formatSeconds(elapsedInSeconds);

            return hoursMinutesSeconds;
        }
        // pendingFilters intentionally omitted — the filter renderers read it via
        // pendingFiltersRef so the columns array stays stable across keystrokes
        // (rebuilding it remounts the filter input and steals focus).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.message_header.start_time, formatSeconds, showAllDetails]);

    /** Creates badges for the top of the page, which can be clicked to filter data */
    const getBadges = () => {
        if (data === undefined) return <></>;

        return (
            <span className={styles.badgeBox}>
                {data.message_header.number_created > 0 && (
                    <>
                        <Badge
                            id="createdBadge"
                            pill
                            color="info"
                            className={`${styles.badge} ${styles.infoBadge}`}
                            style={
                                pendingFilters.created
                                    ? { opacity: "1" }
                                    : { opacity: ".6" }
                            }
                            onClick={() => changeFilters("created")}
                        >
                            {data.message_header.number_created}
                        </Badge>
                        <UncontrolledTooltip target="createdBadge">
                            {`${
                                pendingFilters.created
                                    ? "Hide Created"
                                    : "Show Created"
                            }`}
                        </UncontrolledTooltip>
                    </>
                )}
                {data.message_header.number_updated > 0 && (
                    <>
                        <Badge
                            id="updatedBadge"
                            pill
                            color="success"
                            className={`${styles.badge} ${styles.successBadge}`}
                            style={
                                pendingFilters.updated
                                    ? { opacity: "1" }
                                    : { opacity: ".6" }
                            }
                            onClick={() => changeFilters("updated")}
                        >
                            {data.message_header.number_updated}
                        </Badge>
                        <UncontrolledTooltip target="updatedBadge">
                            {`${
                                pendingFilters.updated
                                    ? "Hide Updated"
                                    : "Show Updated"
                            }`}
                        </UncontrolledTooltip>
                    </>
                )}
                {data.message_header.number_deleted > 0 && (
                    <>
                        <Badge
                            id="deletedBadge"
                            pill
                            color="black"
                            className={`${styles.badge} ${styles.deletedBadge}`}
                            style={
                                pendingFilters.deleted
                                    ? { opacity: "1" }
                                    : { opacity: ".6" }
                            }
                            onClick={() => changeFilters("deleted")}
                        >
                            {data.message_header.number_deleted}
                        </Badge>
                        <UncontrolledTooltip target="deletedBadge">
                            {`${
                                pendingFilters.deleted
                                    ? "Hide Deleted"
                                    : "Show Deleted"
                            }`}
                        </UncontrolledTooltip>
                    </>
                )}
                {data.message_header.number_warning > 0 && (
                    <>
                        <Badge
                            id="warningBadge"
                            pill
                            color="warning"
                            className={`${styles.badge} ${styles.warningBadge}`}
                            style={
                                pendingFilters.warning
                                    ? { opacity: "1" }
                                    : { opacity: ".6" }
                            }
                            onClick={() => changeFilters("warning")}
                        >
                            {data.message_header.number_warning}
                        </Badge>
                        <UncontrolledTooltip target="warningBadge">
                            {`${
                                pendingFilters.warning
                                    ? "Hide Warning"
                                    : "Show Warning"
                            }`}
                        </UncontrolledTooltip>
                    </>
                )}
                {data.message_header.number_errored > 0 && (
                    <>
                        <Badge
                            id="erroredBadge"
                            pill
                            color="danger"
                            className={`${styles.badge} ${styles.dangerBadge}`}
                            style={
                                pendingFilters.errored
                                    ? { opacity: "1" }
                                    : { opacity: ".6" }
                            }
                            onClick={() => changeFilters("errored")}
                        >
                            {data.message_header.number_errored}
                        </Badge>
                        <UncontrolledTooltip target="erroredBadge">
                            {`${
                                pendingFilters.errored
                                    ? "Hide Errored"
                                    : "Show Errored"
                            }`}
                        </UncontrolledTooltip>
                    </>
                )}
                {data.message_header.status.includes("Debug") && (
                    <>
                        <span id="debugBadge">
                            <FontAwesomeIcon
                                className={styles.icon}
                                icon={faBugSlash}
                            />
                        </span>
                        <UncontrolledTooltip target="debugBadge">
                            Debug Mode
                        </UncontrolledTooltip>
                    </>
                )}
                {data.message_header.name.includes("Preview") && (
                    <>
                        <span id="previewBadge">
                            <FontAwesomeIcon
                                className={styles.icon}
                                icon={faWrench}
                            />
                        </span>
                        <UncontrolledTooltip target="previewBadge">
                            Preview Mode
                        </UncontrolledTooltip>
                    </>
                )}
            </span>
        );
    };

    /** Adds icon at top of the page by status h3 */
    const statusIcon = useMemo(() => {
        if (data === undefined) return <></>;

        switch (data.message_header.status) {
            case "Complete":
                return (
                    <FontAwesomeIcon
                        className={`${styles.check} text-success`}
                        icon={faCheck}
                    />
                );
            case "Warning":
                return (
                    <FontAwesomeIcon
                        className={`${styles.check} text-warning`}
                        icon={faExclamationTriangle}
                    />
                );
            case "Errored":
                return (
                    <FontAwesomeIcon
                        className={`${styles.check} text-danger`}
                        icon={faCircleExclamation}
                    />
                );
            case "Abandoned":
                return (
                    <FontAwesomeIcon
                        className={`${styles.check} text-secondary`}
                        icon={faGhost}
                    />
                );
            case "Started":
                return getSpinnerAndCancelBtn();
            case "Debugging":
                return getSpinnerAndCancelBtn();
        }

        function getSpinnerAndCancelBtn() {
            return (
                <>
                    <FontAwesomeIcon
                        className={`${styles.check} spinning text-secondary`}
                        icon={faArrowsRotate}
                    />

                    <FontAwesomeIcon
                        id="cancelBtn"
                        className={`${styles.check} ${styles.cancelBtn}`}
                        icon={faCircleStop}
                        onClick={cancelIntegration}
                    />
                    <UncontrolledTooltip target="cancelBtn">
                        Request Cancelation
                    </UncontrolledTooltip>

                    {canAbandonJobs() && messageHeaderId && (
                        <>
                            {" "}
                            <FontAwesomeIcon
                                icon={faBan}
                                id="abandonJobBtn"
                                name="abandonJobBtn"
                                data-testid="abandonJobBtn"
                                className={styles.abandonBtn}
                                onClick={() => abandonJob(messageHeaderId)}
                            />
                            <UncontrolledTooltip target="abandonJobBtn">
                                Mark Job as Abandoned
                            </UncontrolledTooltip>
                        </>
                    )}
                </>
            );
        }

        /** Cancels a running integration on button click */
        async function cancelIntegration() {
            const response = await post(
                `/api/jobqueue/cancel/${messageHeaderId}`
            );
            const data = await response.json();
            console.log(data);

            if (response.ok) {
                newAlert({
                    color: "info",
                    message: "Cancel integration request sent",
                });
            } else {
                newAlert({
                    color: "danger",
                    message: data.detail,
                });
            }
        }
    }, [abandonJob, canAbandonJobs, data, messageHeaderId, newAlert, post]);

    /** Calculates the proper icon for the rerun integration live button */
    const directionInfo = useMemo(() => {
        if (data === undefined || data.integration == null) return undefined;

        return direction.getDirectionInfo(data.integration.direction);
    }, [data]);

    const toggleAllFilters = (): void => {
        if (showingAllMessageTypes) {
            setPendingFilters((prev) => ({
                ...prev,
                created: false,
                deleted: false,
                updated: false,
                errored: false,
                warning: false,
                info: false,
            }));
        } else {
            setPendingFilters((prev) => ({
                ...prev,
                created: true,
                deleted: true,
                updated: true,
                errored: true,
                warning: true,
                info: true,
            }));
        }

        setShowingAllMessageTypes((prev) => !prev);
    };

    const getRunTime = () => {
        const messageHeader = data?.message_header;
        const value = messageHeader?.run_time ?? 0;

        if (!messageHeader || !messageHeader.start_time)
            return formatSeconds(value);

        if (
            !messageHeader.finish_time &&
            ["Started", "Debugging", "Abandoned"].includes(messageHeader.status)
        ) {
            const start = new Date(messageHeader.start_time);
            const now = new Date();
            let diff = (now.getTime() - start.getTime()) / 1000;
            if (diff < 0) {
                diff = 0;
            }

            return (
                <span>
                    <IntegrationRunTime start_time={diff} />
                </span>
            );
        }
        return formatSeconds(value);
    };

    const serverFetch = useMemo(() => {
        return {
            fetchData: getDetails,
            fetchInterval
        };
    }, [fetchInterval, getDetails]);

    const tableProps = {
        columns,
        data: data?.message_details ?? [],
        isLoading,
        showPagination: true,
        triggerGoToPageOne,
        serverFetch,
        serverPages:
        {
            pageCount: data?.page_count ?? 0,
            totalRecords: data?.total_records ?? 0,
        },
        // Stable key — `/run_details/:id` includes the id in pathname, so without
        // this every run gets its own pageSize and nothing persists.
        pageSizeStorageKey: "/run_details",
    };

    return (
        <Container fluid>
            {/* ===== Method name and status ===== */}
            <Row>
                <Col>
                    <h3 className={styles.nameHeader}>
                        {data?.message_header.name}
                        {data?.integration?.documentation_url && (
                            <>
                                <a
                                    id="docLink"
                                    href={data.integration.documentation_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.docLink}
                                >
                                    <FontAwesomeIcon icon={faFileLines} />
                                </a>
                                <UncontrolledTooltip target="docLink">
                                    Documentation
                                </UncontrolledTooltip>
                            </>
                        )}
                    </h3>
                </Col>
                <Col style={{ textAlign: "right" }}>
                    <h3 className={styles.statusHeader}>
                        {statusIcon}
                        {data?.message_header.status}
                    </h3>
                </Col>
            </Row>
            <br />

            {/* ===== Info box with buttons, badges, filters =====  */}
            <Row>
                <Card className={styles.aboveTableBox}>
                    <Row className="align-items-center">
                        {/* Rerun buttons */}
                        <Col sm="2" className={styles.rerunBtnsBox}>
                            {/* if admin or calance or "RerunAll" can rerun, otherwise disable both*/}
                            <Button
                                id="rerunLiveBtn"
                                data-testid="rerunLiveBtn"
                                className={styles.rerunLiveBtn}
                                name=""
                                style={
                                    directionInfo && {
                                        backgroundColor: directionInfo.color,
                                    }
                                }
                                onClick={runIntegration}
                                disabled={
                                    !canRerunAll(
                                        data?.message_header.initiator ?? ""
                                    )
                                }
                            >
                                <FontAwesomeIcon
                                    icon={
                                        directionInfo
                                            ? directionInfo.icon
                                            : faArrowRotateRight
                                    }
                                />
                            </Button>
                            <UncontrolledTooltip target="rerunLiveBtn">
                                Run integration again - LIVE
                            </UncontrolledTooltip>
                            <Button
                                id="rerunPreviewBtn"
                                data-testid="rerunPreviewBtn"
                                className={styles.rerunPreviewBtn}
                                onClick={runIntegration}
                                disabled={
                                    !canRerunAll(
                                        data?.message_header.initiator ?? ""
                                    )
                                }
                                name="DEBUG"
                            >
                                <FontAwesomeIcon icon={faWrench} />
                            </Button>
                            <UncontrolledTooltip target="rerunPreviewBtn">
                                Run integration again - PREVIEW
                            </UncontrolledTooltip>
                            {isAdmin() && (
                                <>
                                    <Button
                                        id="downloadLogTop"
                                        onClick={getLogFile}
                                    >
                                        <FontAwesomeIcon icon={faFileLines} />
                                    </Button>
                                    <UncontrolledTooltip target="downloadLogTop">
                                        Download Log
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </Col>
                        <Col className={styles.infoBox}>
                            <div>
                                <b>Run Time:</b> {data && getRunTime()}
                            </div>
                            <div>
                                <b>Run By:</b> {data?.message_header.initiator}
                            </div>
                            {/* Only runs stamped by a Runner since dimension-4qgyb carry a version. */}
                            {data?.message_header.runner_version && (
                                <div data-testid="runnerVersion">
                                    <b>Version:</b>{" "}
                                    {data.message_header.runner_version}
                                </div>
                            )}
                        </Col>

                        {/* Badges */}
                        <Col className={styles.badgesBtnsRight}>
                            {getBadges()}

                            {/* Show/Hide info messages */}
                            <Button
                                className={styles.filterBtns}
                                onClick={() => changeFilters("info")}
                                style={
                                    pendingFilters.info
                                        ? { opacity: 1 }
                                        : { opacity: 0.7 }
                                }
                            >
                                {pendingFilters.info
                                    ? "Hide Info"
                                    : "Show Info"}
                            </Button>

                            <Button
                                className={styles.filterBtns}
                                onClick={() => toggleAllFilters()}
                                style={
                                    showingAllMessageTypes
                                        ? { opacity: 1 }
                                        : { opacity: 0.7 }
                                }
                            >
                                {showingAllMessageTypes
                                    ? "Show None"
                                    : "Show All"}
                            </Button>

                            {/* Expand/collapse message details */}
                            <Button
                                className={styles.filterBtns}
                                onClick={() =>
                                    setShowAllDetails((prev) => !prev)
                                }
                                style={
                                    showAllDetails
                                        ? { opacity: 0.7 }
                                        : { opacity: 1 }
                                }
                            >
                                {showAllDetails
                                    ? "Hide Details"
                                    : "Show Details"}
                            </Button>

                            {/* Search Button */}
                            <Button
                                className={styles.filterBtns}
                                id="search"
                                style={{ backgroundColor: "var(--bs-primary)" }}
                                onClick={handleSearch}
                                disabled={
                                    JSON.stringify(pendingFilters) ===
                                    JSON.stringify(appliedFilters)
                                }
                            >
                                Apply Filters &nbsp;
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            </Button>
                            <UncontrolledTooltip target="search">
                                Pressing Enter also applies the filters
                            </UncontrolledTooltip>
                        </Col>
                    </Row>
                </Card>
            </Row>
            <br />

            {/* Table */}
            <Row>
                <ReactTable {...tableProps} />
            </Row>

            {/* Integration parameters */}
            <Row>
                <Col>
                    <br />
                    {`Integration Parameters: ${
                        decodeURIComponent(
                            data?.message_header.method_info ?? ""
                        ) ?? ""
                    }${
                        data?.message_header.machine_name &&
                        ` on ${data.message_header.machine_name}`
                    }`}
                </Col>
            </Row>
        </Container>
    );
}
